import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ArcButton, ArcSkeleton, ArcStack, ArcSwitch, ArcText, ArcToolbar } from '@src/core/ui';
import {
  NOTIFICATION_SETTINGS_SECTIONS,
  NotificationSettingEntity,
  NotificationType,
  useNotificationSettings,
} from '@src/core/features';

const NotificationSettingRow: React.FC<{
  setting: NotificationSettingEntity;
  disabled: boolean;
  onToggle: (type: NotificationType) => void;
}> = ({ setting, disabled, onToggle }) => {
  const { t } = useTranslation();

  return (
    <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
      <ArcText.h5>{t(`notifications.types.${setting.type}`)}</ArcText.h5>
      <ArcSwitch.brand
        checked={setting.enabled}
        disabled={disabled}
        onChange={() => onToggle(setting.type)} />
    </ArcStack>
  );
};

const NotificationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings, isLoading, isSaving, isResetting, onToggle, onReset } = useNotificationSettings();

  const settingsByType = React.useMemo(
    () => new Map(settings.map((setting) => [setting.type, setting])),
    [settings],
  );

  const isDisabled = isSaving || isResetting;

  return (
    <ArcStack p={16} pt={6} spacing={6}>
      <ArcStack
        py={2}
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        borderBottom='1px solid'
        borderColor='divider'>
        <ArcToolbar
          title={t('notifications.settingsPage.title')}
          titleFontSize={20}
          goBack={() => navigate(-1)}
        />
        <ArcButton.outlined
          startIcon={<RestartAltIcon sx={{ color: 'white' }} />}
          disabled={isResetting || isLoading}
          onClick={onReset}>
          {t('notifications.settingsPage.reset')}
        </ArcButton.outlined>
      </ArcStack>

      <ArcStack spacing={5}>
        {isLoading ? (
          NOTIFICATION_SETTINGS_SECTIONS.map((section) => (
            <ArcStack key={section.titleKey} spacing={2}>
              <ArcSkeleton width={120} height={28} />
              <ArcStack spacing={1}>
                {section.types.map((type) => (
                  <ArcSkeleton key={type} height={45} />
                ))}
              </ArcStack>
            </ArcStack>
          ))
        ) : (
          NOTIFICATION_SETTINGS_SECTIONS.map((section) => (
            <ArcStack key={section.titleKey} spacing={2}>
              <ArcText.h3 sx={{textTransform: 'uppercase'}}>{t(section.titleKey)}</ArcText.h3>
              <ArcStack spacing={1}>
                {section.types.map((type) => {
                  const setting = settingsByType.get(type);
                  if (!setting) return null;

                  return (
                    <NotificationSettingRow
                      key={setting.type}
                      setting={setting}
                      disabled={isDisabled}
                      onToggle={onToggle} />
                  );
                })}
              </ArcStack>
            </ArcStack>
          ))
        )}
      </ArcStack>
    </ArcStack>
  );
};

export default NotificationPage;
